import { ContractType, Currency, InvoiceMode, RegionType, RetailVsCodeFilter } from "@constant/ThroughShipment";
import { ListFilter } from "@shared/types";

import CoreAPI from "../CoreAPI";
import { transformAPIKeyToCamel } from "../utils/transformAPIKeyToCamel";
import { transformCamelToSnake } from "../utils/transformCamelToSnake";

const apiClient = new CoreAPI();

// defined api type
export interface RetailPayload {
  region?: number;
  contractTypes?: number[];
  regionTypes?: RegionType;
  isVs?: RetailVsCodeFilter;
  name?: string;
}
export interface RetailList {
  id: number;
  name: string;
  address: string;
  phone: string;
  region: {
    id: number;
    name: string;
    regionType: RegionType;
  };
  invoiceMode?: InvoiceMode;
}
export interface RetailListResult {
  count: number;
  next: string | null;
  previous: string | null;
  results: RetailList[];
}
export interface AddRetailProductLinePayload {
  name: string;
}
export interface RetailRegionPayload {
  name?: string;
  regionType?: RegionType;
}
export interface RegionListResult {
  count: number;
  next: string | null;
  previous: string | null;
  results: {
    id: number;
    name: string;
    regionType: RegionType;
  }[];
}
export interface CreateRegionPayload {
  name: string;
  regionType: RegionType;
}
export interface CreateRegion {
  id: number;
  name: string;
  regionType: RegionType;
}
export interface RetailProductLinePayload {
  name?: string;
  nameQ?: string;
}
export interface RetailProductLineListResult {
  count: number;
  next: string | null;
  previous: string | null;
  results: {
    id: number;
    name: string;
  }[];
}
export interface DealerFilter extends ListFilter {
  regionType?: RegionType;
  retailer?: number;
  regionName?: string;
  contractType?: ContractType;
  currency?: Currency;
  productLine?: number;
}
export interface DealerList {
  id: number;
  retailer: {
    id: number;
    name: string;
    address: string;
    phone: string;
    region: {
      id: number;
      name: string;
      regionType: RegionType;
    };
    erpCustomerName: string;
    invoiceMode?: InvoiceMode;
  };
  productLine: {
    id: number;
    name: string;
  };
  currency: Currency;
  contractType: ContractType;
  memo: string;
  daysAfterMonthlyStatement: number;
  taxIncluded: boolean;
}
export interface DealerListResult {
  count: number;
  next: string | null;
  previous: string | null;
  results: DealerList[];
}
export interface UpdateDealerPayload {
  address: string;
  phone: string;
  currency: Currency;
  contractType: ContractType;
  memo: string;
  daysAfterMonthlyStatement: number;
  invoiceMode?: InvoiceMode;
}
export interface CreateDealerPayload extends UpdateDealerPayload {
  name: string;
  region: RegionType;
  productLine: number;
  erpCustomerName: string;
}
export interface ContactInfoListFilter extends ListFilter {
  retailerProductline?: number;
  retailer?: number;
  productlines?: string;
}
export interface ContactInfoList {
  id: number;
  name: string;
  title: string;
  phone: string;
  email: string;
  isMajor: boolean;
  retailerProductline: number;
}
export interface ContactInfoListResult {
  count: number;
  next: string | null;
  previous: string | null;
  results: ContactInfoList[];
}
export interface CreateContactInfoPayload {
  name: string;
  title: string;
  phone: string;
  email: string;
  isMajor?: boolean;
  retailerProductline: number;
}
export interface UpdateContactInfoPayload {
  name?: string;
  title?: string;
  phone?: string;
  email?: string;
  isMajor?: boolean;
}
export interface DeductionList {
  id: number;
  name: string;
  rate: number;
  amount: number;
  retailerProductline: number;
  isActive: boolean;
  deductionType: number;
}
export interface DeductionListResult {
  count: number;
  next: string | null;
  previous: string | null;
  results: DeductionList[];
}
export interface UpdateDeductionPayload {
  name?: string;
  rate: number;
  amount?: number;
  retailerProductline?: number;
  isActive: boolean;
  deductionType: number;
}

export interface BatchUpdateDeductionPayload {
  retailerId: number;
  deductions: {
    rate: number;
    isActive: boolean;
    deductionType: number;
  }[];
}
export interface AccessPointListPayload extends ListFilter {
  name?: string;
  nameQ?: string;
  retailer?: number;
  productlines?: string;
  retailerProductline?: number;
}
export interface AccessPointList {
  id: number;
  name: string;
  address: string;
  retailerProductline: number;
  code: string;
}
export interface AccessPointListResult {
  count: number;
  next: string | null;
  previous: string | null;
  results: AccessPointList[];
}
export interface CreateAccessPointPayload {
  name: string;
  address: string;
  code: string;
  retailerProductline: number;
}
export interface UpdateAccessPointPayload {
  name: string;
  code: string;
  address: string;
}
export interface ContractList {
  id: number;
  name: string;
  startAt: string;
  endAt: string;
  media: string;
  retailerProductline: number;
}
export interface ContractListResult {
  count: number;
  next: string | null;
  previous: string | null;
  results: ContractList[];
}
export interface CreateContractPayload {
  name: string;
  startAt: string;
  endAt: string;
  pdfDirPrefix?: string;
  pdfFilePath?: string;
  retailerProductline: number;
}
export interface UpdateContractPayload extends CreateContractPayload {}

interface ThroughShipmentApi {
  // 通路
  fetchRetailRegion: (payload: RetailRegionPayload) => Promise<RegionListResult>;
  fetchCreateRegion: (payload: CreateRegionPayload) => Promise<CreateRegion>;
  fetchRetailList: (payload: RetailPayload) => Promise<RetailListResult>;
  fetchRetailProductLine: (payload?: RetailProductLinePayload) => Promise<RetailProductLineListResult>;
  fetchAddRetailProductLine: (payload: AddRetailProductLinePayload) => Promise<void>;
  fetchDealerList: (payload: DealerFilter) => Promise<DealerListResult>;
  fetchDealerInfo: (delearId: number) => Promise<DealerList>;
  fetchCreateDealer: (payload: CreateDealerPayload) => Promise<DealerList>;
  fetchUpdateDealer: (delearId: number, payload: UpdateDealerPayload) => Promise<void>;
  fetchDeleteDealer: (delearId: number) => Promise<void>;
  fetchContactInfoList: (payload: ContactInfoListFilter) => Promise<ContactInfoListResult>;
  fetchCreateContact: (payload: CreateContactInfoPayload) => Promise<void>;
  fetchUpdateContact: (contactId: number, payload: UpdateContactInfoPayload) => Promise<void>;
  fetchDeleteContact: (contactId: number) => Promise<void>;
  fetchDeductionFeeList: (
    payload: Partial<ListFilter> & { retailer?: number; retailerProductline?: number },
  ) => Promise<DeductionListResult>;
  fetchUpdateDeductionFee: (deductionId: number, payload: UpdateDeductionPayload) => Promise<void>;
  fetchBatchUpdateDeductionFee: (payload: BatchUpdateDeductionPayload) => Promise<void>;
  fetchAccessPointList: (payload: AccessPointListPayload) => Promise<AccessPointListResult>;
  fetchCreateAccessPoint: (payload: CreateAccessPointPayload) => Promise<void>;
  fetchUpdateAccessPoint: (accessPointId: number, payload: UpdateAccessPointPayload) => Promise<void>;
  fetchDeleteAccessPoint: (accessPointId: number) => Promise<void>;
  fetchContractList: (retailerId: number, payload: ListFilter) => Promise<ContractListResult>;
  fetchCreateContract: (payload: CreateContractPayload) => Promise<void>;
  fetchUpdateContract: (contractId: number, payload: UpdateContractPayload) => Promise<void>;
  fetchDeleteContract: (contractId: number) => Promise<void>;
  fetchErpCustomerList: () => Promise<string[]>;
}

const throughShipmentApi: ThroughShipmentApi = {
  fetchRetailRegion: async (payload) => {
    const response = await apiClient.getData("/manage/retail/regions/", transformCamelToSnake(payload));
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchCreateRegion: async (payload) => {
    const response = await apiClient.postData("/manage/retail/regions/", transformCamelToSnake(payload));
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchRetailList: async (payload) => {
    const getParams = {
      ...payload,
      contractTypes: payload?.contractTypes?.join(","),
      name: payload?.name || undefined,
    };
    const response = await apiClient.getData("/manage/retail/retailers/", transformCamelToSnake(getParams));
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchRetailProductLine: async (payload) => {
    const response = await apiClient.getData(
      "/manage/retail/product-lines/",
      payload ? transformCamelToSnake(payload) : undefined,
    );
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchAddRetailProductLine: async (payload) => {
    await apiClient.postData("/manage/retail/product-lines/", payload);
  },
  fetchDealerList: async (payload) => {
    const response = await apiClient.getData("/manage/retail/retailer-relations/", transformCamelToSnake(payload));
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchDealerInfo: async (delearId) => {
    const response = await apiClient.getData(`/manage/retail/retailer-relations/${delearId}/`, {});
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchCreateDealer: async (payload) => {
    const response = await apiClient.postData("/manage/retail/retailer-relations/", transformCamelToSnake(payload));
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchUpdateDealer: async (delearId, payload) => {
    await apiClient.patchData(`/manage/retail/retailer-relations/${delearId}/`, transformCamelToSnake(payload));
  },
  fetchDeleteDealer: async (delearId) => {
    await apiClient.deleteData(`/manage/retail/retailer-relations/${delearId}/`, {});
  },
  fetchContactInfoList: async (payload) => {
    const response = await apiClient.getData("/manage/retail/retailer-contacts/", transformCamelToSnake(payload));
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchCreateContact: async (payload) => {
    await apiClient.postData("/manage/retail/retailer-contacts/", transformCamelToSnake(payload));
  },
  fetchUpdateContact: async (contactId, payload) => {
    await apiClient.patchData(`/manage/retail/retailer-contacts/${contactId}/`, transformCamelToSnake(payload));
  },
  fetchDeleteContact: async (contactId) => {
    await apiClient.deleteData(`/manage/retail/retailer-contacts/${contactId}/`, {});
  },
  fetchDeductionFeeList: async (payload) => {
    const response = await apiClient.getData("/manage/retail/retailer-deductions/", transformCamelToSnake(payload));
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchUpdateDeductionFee: async (deductionId, payload) => {
    await apiClient.patchData(`/manage/retail/retailer-deductions/${deductionId}/`, transformCamelToSnake(payload));
  },
  fetchBatchUpdateDeductionFee: async (payload) => {
    await apiClient.putData("/manage/retail/retailer-deductions/batch-update/", transformCamelToSnake(payload));
  },
  fetchAccessPointList: async (payload) => {
    const response = await apiClient.getData("/manage/retail/retailer-warehouses/", transformCamelToSnake(payload));
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchCreateAccessPoint: async (payload) => {
    await apiClient.postData("/manage/retail/retailer-warehouses/", transformCamelToSnake(payload));
  },
  fetchUpdateAccessPoint: async (accessPointId, payload) => {
    await apiClient.patchData(`/manage/retail/retailer-warehouses/${accessPointId}/`, transformCamelToSnake(payload));
  },
  fetchDeleteAccessPoint: async (accessPointId) => {
    await apiClient.deleteData(`/manage/retail/retailer-warehouses/${accessPointId}/`, {});
  },
  fetchContractList: async (retailerId, payload) => {
    const response = await apiClient.getData(
      `/manage/retail/retailer-contracts/?retailer_productline=${retailerId}`,
      payload,
    );
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchCreateContract: async (payload) => {
    await apiClient.postData("/manage/retail/retailer-contracts/", transformCamelToSnake(payload));
  },
  fetchUpdateContract: async (contractId, payload) => {
    await apiClient.patchData(`/manage/retail/retailer-contracts/${contractId}/`, transformCamelToSnake(payload));
  },
  fetchDeleteContract: async (contractId) => {
    await apiClient.deleteData(`/manage/retail/retailer-contracts/${contractId}/`, {});
  },
  fetchErpCustomerList: async () => {
    const response = await apiClient.getData("/manage/erp/customers", {});
    return transformAPIKeyToCamel(response.data.result?.name);
  },
};

export default throughShipmentApi;
